<script lang="ts">
	import Layout from "@components/layout/Layout.svelte";
	import type { BlogPosting } from "schema-dts";
	import { getBlogPath } from "@core/schema/paths/getBlogPath.js";
	import { getUploadedFilePath } from "@core/utils/getUploadedFilePath.js";
	import { DateTime } from "@core/utils/luxon.js";
	import Attachments from "../components/common/Attachments.svelte";
	import BlogArticleCard from "../components/common/BlogArticleCard.svelte";
	import CkEditorContent from "../components/common/CkEditorContent.svelte";
	import Formatted from "../components/common/Formatted.svelte";
	import Link from "../components/common/Link.svelte";
	import Media from "../components/common/Media.svelte";
	import StructuredData from "../components/common/StructuredData.svelte";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { calculateReadingTime } from "../utils/calculateReadingTime.js";
	import type { BlogDetailProps } from "./BlogDetailProps.js";

	const { article, relatedArticles }: BlogDetailProps = $props();
	const { WEB_URL, FILE_BUCKET_URL_MAP } = getGlobalSettings();

	const formattedDate = $derived(DateTime.fromISO(article.publishedAt).setLocale("cs").toFormat("d. MMMM yyyy"));
	const blogUrl = $derived(getBlogPath(article.locale));
	const readingTime = $derived(calculateReadingTime(article.content, article.perex));

	const articleSchema: BlogPosting = {
		"@type": "BlogPosting",
		headline: article.title,
		description: article.perex,
		image: article.primaryImage
			? getUploadedFilePath(article.primaryImage, FILE_BUCKET_URL_MAP)
			: `${WEB_URL}/og-image.jpg`,
		datePublished: article.publishedAt,
		dateModified: article.publishedAt,
		author: {
			"@type": "Organization",
			name: "Head Spa Napohodu",
			url: WEB_URL,
		},
		publisher: {
			"@type": "Organization",
			name: "Head Spa Napohodu",
			url: WEB_URL,
			logo: {
				"@type": "ImageObject",
				url: `${WEB_URL}/favicon.ico`,
			},
		},
		mainEntityOfPage: {
			"@type": "WebPage",
			"@id": `${WEB_URL}/blog/${article.urlSlug}`,
		},
	};
</script>

<StructuredData data={articleSchema} />

<Layout>
	<article class="full-width pt-16 pb-8 md:pb-12 mb:pt-16 lg:py-16">
		<div class="px-4 md:px-8 max-w-7xl mx-auto mb-4 md:mb-8">
			<nav class="flex items-center gap-2 text-sm text-[#7D7D7D]">
				<Link href={blogUrl} class="hover:text-hazelnut-600 transition-colors flex-shrink-0">Blog</Link>
				<span class="flex-shrink-0">/</span>
				<span class="text-[#262626] truncate">{article.title}</span>
			</nav>
		</div>

		<div class="px-4 md:px-8 max-w-7xl mx-auto mb-6 md:mb-12">
			<div class="flex flex-col gap-6 md:gap-8">
				<div class="flex flex-col gap-4 max-w-3xl md:mb-8">
					<h1 class="text-xl md:text-4xl lg:text-5xl font-bold leading-tight md:mt-6 mb-5">
						<Formatted text={article.title} />
					</h1>

					<div class="flex items-center gap-4 text-sm text-[#7D7D7D]">
						<time datetime={article.publishedAt} class="font-medium">
							{formattedDate}
						</time>
						<span>•</span>
						<span>{readingTime} {readingTime === 1 ? "minuta" : readingTime < 5 ? "minuty" : "minut"} čtení</span>
					</div>

					{#if article.perex}
						<p class="text-sm md:text-[30px]/[1.5] text-[#262626] font-light"><Formatted text={article.perex} /></p>
					{/if}
				</div>

				{#if article.primaryImage}
					<div class="w-full aspect-[16/9] rounded-2xl md:rounded-3xl overflow-hidden">
						<Media
							source={article.primaryImage}
							alt={article.title}
							width={1200}
							height={675}
							sourcesets={{}}
							class="w-full h-full object-cover"
						/>
					</div>
				{/if}
			</div>
		</div>

		<div class="px-4 md:px-8 max-w-7xl mx-auto">
			<div class="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
				<div class="lg:col-span-8">
					<CkEditorContent
						html={article.content}
						lightboxId="blog-{article.id}"
						class="prose max-w-none blog-content
					prose-headings:font-bold prose-headings:text-[#262626] prose-headings:mb-6 prose-headings:mt-8
					prose-h2:text-2xl prose-h2:md:text-3xl prose-h2:leading-tight
					prose-h3:text-xl prose-h3:md:text-2xl prose-h3:leading-tight
					prose-h4:text-lg prose-h4:md:text-xl prose-h4:leading-tight
					prose-h5:text-base prose-h5:md:text-lg prose-h5:leading-tight prose-h5:font-bold
					prose-h6:text-base prose-h6:md:text-lg prose-h6:leading-tight prose-h6:font-bold
					prose-p:text-lg prose-p:md:text-[1.25rem] prose-p:text-[#262626] prose-p:leading-relaxed prose-p:mb-6 prose-p:font-normal
					prose-a:text-hazelnut-600 prose-a:no-underline hover:prose-a:underline prose-a:font-normal
					prose-strong:text-[#262626] prose-strong:font-semibold
					prose-ul:my-6 prose-ul:list-disc prose-ul:pl-6 prose-ul:text-[#262626]
					prose-ol:my-6 prose-ol:list-decimal prose-ol:pl-6 prose-ol:text-[#262626]
					prose-li:text-base prose-li:md:text-lg prose-li:leading-relaxed prose-li:mb-2 prose-li:font-normal"
					/>

					{#if article.images.length > 0}
						<div class="mt-6 md:mt-12">
							<h3 class="text-lg md:text-2xl font-bold mb-6">Galerie</h3>
							<div class="grid grid-cols-2 md:grid-cols-3 gap-4">
								{#each article.images as image (image.path)}
									<div class="aspect-square rounded-xl overflow-hidden">
										<Media
											source={image}
											alt={`${article.title} - obrázek`}
											width={400}
											height={400}
											sourcesets={{}}
											lightbox="blog-gallery-{article.id}"
											lightboxWidth={1920}
											class="w-full h-full object-cover cursor-pointer"
										/>
									</div>
								{/each}
							</div>
						</div>
					{/if}

					{#if article.attachments.length > 0}
						<div class="mt-6 md:mt-12 p-6 bg-ivory-50 rounded-2xl">
							<h3 class="text-xl font-bold mb-4">Přílohy ke stažení</h3>
							<Attachments attachments={article.attachments} />
						</div>
					{/if}

					<div class="mt-6 md:mt-12 pt-4 md:pt-8 border-t border-ivory-300">
						<Link href={blogUrl} class="inline-flex items-center gap-2 text-hazelnut-600 hover:text-hazelnut-700">
							<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
							</svg>
							Zpět na blog
						</Link>
					</div>
				</div>

				<aside class="lg:col-span-4">
					<div class="lg:sticky lg:top-20">
						{#if relatedArticles.length > 0}
							<div class="bg-ivory-50 rounded-2xl p-6 pt-1">
								<h3 class="text-xl font-bold mb-6">Další články</h3>
								<div class="flex flex-col gap-6">
									{#each relatedArticles as relatedArticle (relatedArticle.id)}
										<BlogArticleCard article={relatedArticle} compact />
									{/each}
								</div>
							</div>
						{/if}
					</div>
				</aside>
			</div>
		</div>
	</article>
</Layout>

<style>
	:global(.blog-content ul li),
	:global(.blog-content ol li) {
		font-size: 1rem !important;
		color: #262626 !important;
		margin-bottom: 0.5rem !important;
		font-weight: 400 !important;
		line-height: 1.75 !important;
	}

	:global(.blog-content ul li p),
	:global(.blog-content ol li p) {
		font-size: 1rem !important;
		margin-bottom: 0 !important;
		font-weight: 400 !important;
		line-height: 1.75 !important;
	}

	@media (min-width: 48rem) {
		:global(.blog-content ul li),
		:global(.blog-content ol li) {
			font-size: 1.25rem !important;
		}

		:global(.blog-content ul li p),
		:global(.blog-content ol li p) {
			font-size: 1.25rem !important;
		}
	}

	:global(.article__heading) {
		scroll-margin-top: 3rem;
		font-weight: 700 !important;
		color: #262626 !important;
		line-height: 1.25 !important;
	}

	:global(.article__heading--2) {
		font-size: 1.5rem !important;
		margin-top: 2rem !important;
		margin-bottom: 1.5rem !important;
	}

	@media (min-width: 48rem) {
		:global(.article__heading--2) {
			font-size: 1.875rem !important;
		}
	}

	:global(.article__heading--3) {
		font-size: 1.25rem !important;
		margin-top: 2rem !important;
		margin-bottom: 1.5rem !important;
	}

	@media (min-width: 48rem) {
		:global(.article__heading--3) {
			font-size: 1.5rem !important;
		}
	}

	:global(.article__heading--4) {
		font-size: 1.125rem !important;
		margin-top: 2rem !important;
		margin-bottom: 1.5rem !important;
	}

	@media (min-width: 48rem) {
		:global(.article__heading--4) {
			font-size: 1.25rem !important;
		}
	}

	:global(.article__heading--5),
	:global(.article__heading--6) {
		font-size: 1rem !important;
		margin-top: 2rem !important;
		margin-bottom: 1.5rem !important;
	}

	@media (min-width: 48rem) {
		:global(.article__heading--5),
		:global(.article__heading--6) {
			font-size: 1.125rem !important;
		}
	}

	:global(.blog-content p) {
		font-size: 1.125rem !important;
		font-weight: 400 !important;
		line-height: 1.75 !important;
	}

	@media (min-width: 48rem) {
		:global(.blog-content p) {
			font-size: 1.25rem !important;
		}
	}

	:global(.blog-content strong) {
		font-weight: 600 !important;
	}

	:global(.blog-content a) {
		font-weight: 400 !important;
	}

	:global(.blog-content img) {
		margin-top: 2rem !important;
		margin-bottom: 2rem !important;
		border-radius: 1rem !important;
	}

	@media (min-width: 48rem) {
		:global(.blog-content img) {
			margin-top: 3rem !important;
			margin-bottom: 3rem !important;
			border-radius: 1.5rem !important;
		}
	}
</style>
